import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory px-6 py-12">
      <div className="w-full max-w-md">
        <Logo />
        {sent ? (
          <div className="mt-12 text-center" data-testid="forgot-password-sent">
            <MailCheck className="mx-auto h-12 w-12 text-gold-500" />
            <h1 className="mt-6 font-serif text-3xl font-bold text-navy-900">Check your email</h1>
            <p className="mt-3 text-navy-500">
              If an account exists for <span className="font-medium text-navy-800">{email}</span>, a password
              reset link is on its way. The link expires in 1 hour.
            </p>
            <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold-600 hover:text-gold-700">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-12">
              <h1 className="font-serif text-4xl font-bold tracking-tight text-navy-900">Forgot password</h1>
              <p className="mt-3 text-navy-500">
                Enter the email you use to sign in and we'll send you a secure link to reset your password.
              </p>
            </div>
            <form onSubmit={submit} className="mt-10 space-y-5">
              <div>
                <Label htmlFor="email" className="text-navy-800">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="forgot-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@rcc-life.com.au"
                  className="mt-1.5 h-12 rounded-xl border-navy-100 bg-white focus-visible:ring-gold-500"
                  required
                />
              </div>
              {error && (
                <div data-testid="forgot-error" className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                data-testid="forgot-submit-button"
                disabled={loading}
                className="h-12 w-full rounded-xl bg-gold-500 text-white hover:bg-gold-600 font-semibold text-base shadow-ambient"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold-600 hover:text-gold-700">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
